import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { CalendarCheckIcon, FileSignatureIcon, ListTreeIcon, RadioTowerIcon } from 'lucide-react';
import { VignetteFrame } from './VignetteFrame';
const sources: string[] = ['Zillow', 'Realtor.com', 'Your website', 'Sign calls', 'Past clients'];
const destinations: {
  title: string;
  detail: string;
  icon: typeof CalendarCheckIcon;
}[] = [{
  title: 'Agent calendar',
  detail: 'booked by territory',
  icon: CalendarCheckIcon
}, {
  title: 'Your CRM',
  detail: 'every word logged',
  icon: ListTreeIcon
}, {
  title: 'Touring agreement',
  detail: 'signed before the showing',
  icon: FileSignatureIcon
}];
interface FlowDotProps {
  delay: number;
  vertical?: boolean;
}
function FlowDot({
  delay,
  vertical = false
}: FlowDotProps) {
  const reduceMotion = useReducedMotion();
  if (reduceMotion) return null;
  return <motion.span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
    willChange: 'transform, opacity'
  }} initial={vertical ? {
    y: '0%',
    opacity: 0
  } : {
    x: '0%',
    opacity: 0
  }} animate={vertical ? {
    y: ['0%', '100%'],
    opacity: [0, 1, 1, 0]
  } : {
    x: ['0%', '100%'],
    opacity: [0, 1, 1, 0]
  }} transition={{
    duration: 2.6,
    delay,
    repeat: Infinity,
    ease: 'linear'
  }} data-id="element-347" data-mp="2n4c9g8vx041b">
      <span className={`absolute h-1.5 w-1.5 rounded-full bg-brand ${vertical ? 'left-1/2 top-0 -translate-x-1/2' : 'left-0 top-1/2 -translate-y-1/2'}`} data-id="element-348" data-mp="3ickhgex3cq42" />
    </motion.span>;
}
function SourceChip({
  label
}: {
  label: string;
}) {
  return <div className="flex h-9 items-center rounded-lg border border-white/10 bg-ink-700 px-3 text-xs font-medium text-white/70" data-id="element-349" data-mp="39i644hw4fpnl">
      {label}
    </div>;
}
function CenterNode() {
  const reduceMotion = useReducedMotion();
  return <div className="relative rounded-xl border border-brand/40 bg-brand/10 p-4 text-center" data-id="element-350" data-mp="2umhqy38trs7k">
      {!reduceMotion && <motion.span aria-hidden="true" className="absolute inset-0 rounded-xl border border-brand/40" style={{
      willChange: 'transform, opacity'
    }} animate={{
      opacity: [0.5, 0, 0.5],
      scale: [1, 1.04, 1]
    }} transition={{
      duration: 2.8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-351" data-mp="2kycgiaogr6xr" />}
      <span className="relative mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-brand" data-id="element-352" data-mp="2g2dng9s6x0r0">
        <RadioTowerIcon className="h-5 w-5 text-ink-900" aria-hidden="true" data-id="element-353" data-mp="33s7xykp1ed83" />
      </span>
      <p className="relative mt-3 font-display text-sm font-semibold text-white" data-id="element-354" data-mp="38r2qiect3g5e">
        Viridia AI
      </p>
      <p className="relative mt-1 text-xs leading-relaxed text-white/60" data-id="element-355" data-mp="2uvqw682kbrp0">
        answers live, qualifies, routes
      </p>
    </div>;
}
function DestinationCard({
  item
}: {
  item: {
    title: string;
    detail: string;
    icon: typeof CalendarCheckIcon;
  };
}) {
  const Icon = item.icon;
  return <div className="flex items-start gap-3 rounded-lg border border-white/10 bg-ink-700 px-3 py-2.5" data-id="element-356" data-mp="3rhkde2ayjhiu">
      <Icon className="mt-0.5 h-4 w-4 flex-none text-brand" aria-hidden="true" data-id="element-357" data-mp="1fy4gh3hp84v7" />
      <div data-id="element-358" data-mp="1b14bkvwl585o">
        <p className="text-xs font-semibold text-white" data-id="element-359" data-mp="1azbbjzgh9m6n">{item.title}</p>
        <p className="mt-0.5 text-[11px] text-white/50" data-id="element-360" data-mp="1ppr1frju9ib5">{item.detail}</p>
      </div>
    </div>;
}
export function LeadFlowDiagram() {
  return <div data-id="element-361" data-mp="x1d1h696wbwy">
      <VignetteFrame label="System map" timestamp="How it connects" data-id="element-362" data-mp="x1395c0o9so3">
        <div className="hidden lg:grid lg:grid-cols-[1fr_64px_minmax(170px,200px)_64px_1fr] lg:items-center" data-id="element-363" data-mp="x3g18i059jev">
          <div className="space-y-2" data-id="element-364" data-mp="16zsuxfvovx9m">
            {sources.map(source => <SourceChip key={source} label={source} data-id="element-365" data-mp="16xpue1wwulqn" />)}
          </div>

          <div className="space-y-2" data-id="element-366" data-mp="11zeilln2215s">
            {sources.map((source, index) => <div key={source} className="relative flex h-9 items-center" data-id="element-367" data-mp="2idegkr2cbgqe" data-mpi={index}>
                <span className="h-px w-full bg-white/10" aria-hidden="true" data-id="element-368" data-mp="2dgo4sfxkj7as" />
                <FlowDot delay={index * 0.35} data-id="element-369" data-mp="mz10qyvrwwf2" />
              </div>)}
          </div>

          <CenterNode data-id="element-370" data-mp="rxkjkqf2ls37" />

          <div className="flex flex-col justify-around self-stretch py-2" data-id="element-371" data-mp="ryvpavs2t9wy">
            {destinations.map((item, index) => <div key={item.title} className="relative flex h-9 items-center" data-id="element-372" data-mp="vy4hhw7gwzqe" data-mpi={index}>
                <span className="h-px w-full bg-white/10" aria-hidden="true" data-id="element-373" data-mp="10zbr80t688zp" />
                <FlowDot delay={0.6 + index * 0.45} data-id="element-374" data-mp="15yex1ngz6j8q" />
              </div>)}
          </div>

          <div className="space-y-2" data-id="element-375" data-mp="1avwvgjxd9g2h">
            {destinations.map(item => <DestinationCard key={item.title} item={item} data-id="element-376" data-mp="1fmwsdp6hjy4z" />)}
          </div>
        </div>

        <div className="lg:hidden" data-id="element-377" data-mp="1jpjnbyygdz6e">
          <div className="grid grid-cols-2 gap-2" data-id="element-378" data-mp="1joidtyx35kqs">
            {sources.map(source => <SourceChip key={source} label={source} data-id="element-379" data-mp="1opfw0bi3iesa" />)}
          </div>

          <div className="relative mx-auto flex h-10 w-full justify-center" data-id="element-380" data-mp="1to97jydtnvpt">
            <span className="h-full w-px bg-white/10" aria-hidden="true" data-id="element-381" data-mp="23rnzvnt3mxv0" />
            <FlowDot delay={0} vertical data-id="element-382" data-mp="1ys1lvc5g5dl5" />
            <FlowDot delay={1.1} vertical data-id="element-383" data-mp="1yr082zx1qczq" />
          </div>

          <CenterNode data-id="element-384" data-mp="1tsysioytwvt0" />

          <div className="relative mx-auto flex h-10 w-full justify-center" data-id="element-385" data-mp="1ts6uz8lo8o03">
            <span className="h-full w-px bg-white/10" aria-hidden="true" data-id="element-386" data-mp="1prmy8650fmeh" />
            <FlowDot delay={0.5} vertical data-id="element-387" data-mp="1ptg95xmnossa" />
            <FlowDot delay={1.6} vertical data-id="element-388" data-mp="2mhvxjqe14w3t" />
          </div>

          <div className="space-y-2" data-id="element-389" data-mp="2mjgsvi864j2q">
            {destinations.map(item => <DestinationCard key={item.title} item={item} data-id="element-390" data-mp="2hjb5wfcufkqz" />)}
          </div>
        </div>
      </VignetteFrame>

      <p className="mt-5 text-sm text-white/55" data-id="element-391" data-mp="28q7ndsncchph">
        Every lead source, one answering layer, three guaranteed outcomes.
      </p>
    </div>;
}